import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth, user } from '@angular/fire/auth';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { BehaviorSubject, Observable, catchError, filter, from, retry, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface NotificationPayload {
    notification?: {
        title: string;
        body: string;
        icon?: string;
    };
    data?: {
        title?: string;
        body?: string;
        sound?: string;
        messageId?: string;
        [key: string]: any;
    };
}

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {

    public apiUrl = environment.API_URL;

    private currentTokenSubject = new BehaviorSubject<string | null>(null);
    public currentToken$ = this.currentTokenSubject.asObservable();
    public messagePayload$ = new BehaviorSubject<NotificationPayload | null>(null); // For foreground messages
    public notificationUpdate$ = new BehaviorSubject<boolean>(false); // For triggering UI updates
    private backgroundChannel!: BroadcastChannel;

    constructor(private messaging: Messaging, private auth: Auth, private http: HttpClient) {
        this.initializeBroadcastChannel();
        this.setupForegroundMessageListener();
        this.setupAuthListener();
    }

    private initializeBroadcastChannel() {
        // Listen for background notifications from service worker
        this.backgroundChannel = new BroadcastChannel('bg-notification-channel');
        this.backgroundChannel.addEventListener('message', (event) => {
            // Forward background notifications to the same stream as foreground ones
            this.messagePayload$.next(event.data);
        });
    }

    private setupForegroundMessageListener() {
        onMessage(this.messaging, (payload: any) => {
            // Convert Firebase payload to our NotificationPayload format
            const notificationPayload: NotificationPayload = {
                notification: payload.notification,
                data: payload.data
            };
            // Show notification and play sound for foreground messages
            this.handleForegroundNotification(notificationPayload);
            this.messagePayload$.next(notificationPayload);
        });
    }

    private setupAuthListener() {
        user(this.auth).subscribe(firebaseUser => {
            if (firebaseUser) {
                this.requestPermissionAndGetToken(firebaseUser.uid).subscribe();
            } else {
                this.currentTokenSubject.next(null);
                this.deleteFCMToken();
            }
        });
    }

    private handleForegroundNotification(payload: NotificationPayload) {
        // Request notification permission if not already granted
        if (Notification.permission === 'granted') {
            // Show browser notification even when app is in foreground
            this.showBrowserNotification(payload);
        }
    }

    private showBrowserNotification(payload: NotificationPayload) {
        const title = payload.notification?.title || payload.data?.title || 'New Notification';
        const options: NotificationOptions = {
            body: payload.notification?.body || payload.data?.title || 'You have a new message.',
            icon: payload.notification?.icon || '/favicon.ico',
            badge: '/icons/badge-icon.png',
            tag: payload.data?.messageId || 'foreground-notification',
            requireInteraction: true,
            data: payload.data || {},
            silent: false
        };

        const notification = new Notification(title, options);

        notification.onclick = () => {
            notification.close();
            // Handle click - you can customize this based on your needs
            window.focus();
            this.handleNotificationClick(payload.data);
        };

        // Auto close after 8 seconds
        setTimeout(() => {
            notification.close();
        }, 8000);
    }

    private handleNotificationClick(data: any) {
        // Custom logic for handling notification clicks
        if (data?.title?.toLowerCase().includes('booking')) {
            window.location.href = '/after-login/vehicle-booking';
        } else if (data?.title?.toLowerCase().includes('trip')) {
            window.location.href = '/after-login/trip-details';
        }
    }

    requestPermissionAndGetToken(userId: string): Observable<string | null> {

        return from(Notification.requestPermission()).pipe(
            tap(permission => {
                if (permission === 'granted') {
                } else {
                }
            }),
            filter(permission => permission === 'granted'),
            switchMap(() => from(getToken(this.messaging, { vapidKey: environment.firebaseConfig.vapid }))),
            tap((token: string | null) => {
                if (token) {
                    this.currentTokenSubject.next(token);
                    this.sendTokenToBackend(token);
                } else {
                }
            })
        );
    }

    private sendTokenToBackend(token: string): void {
        const payload = {
            "deviceToken": token
        };

        this.http.patch(`${this.apiUrl}${environment.UPDATE_DEVICETOKEN}`, payload).subscribe({
            next: (response) => {
            },
            error: (error) => {
            }
        });

    }

    deleteFCMToken(): void {

    }

    initializeForCurrentUser(): void {
        user(this.auth).subscribe(firebaseUser => {
            if (firebaseUser) {
                this.requestPermissionAndGetToken(firebaseUser.uid).subscribe();
            } else {
            }
        });
    }

    getnotification(): Observable<any> {
        return this.http.get(`${this.apiUrl}${environment.NOTIFICATION_LIST}`)
            .pipe(retry(1), catchError(this.errorHandler));
    }

    updateNotification(id: string): Observable<any> {
        return this.http.patch(`${this.apiUrl}/users/notification/${id}`, {})
            .pipe(retry(1), catchError(this.errorHandler));
    }

    getUnreadNotificationCount(): Observable<number> {
        return this.http.get<number>(`${this.apiUrl}${environment.UNREAD_NOTIFICATION_COUNT}`)
            .pipe(retry(1), catchError(this.errorHandler));
    }

    private errorHandler(error: any): Observable<never> {
        return throwError(error);
    }
}
